import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteGmailDeletionModels } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { GmailDeletionModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteGmailDeletionsDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  selectedModels: number[];
  setSelectedGmailDeletions: Dispatch<SetStateAction<number[]>>;
}

export default function DeleteGmailDeletionsDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedModels,
  setSelectedGmailDeletions,
}: DeleteGmailDeletionsDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const moreThanOneDeletions = selectedModels.length > 1;

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteGmailDeletionModels(selectedModels, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedModels.length > 1
            ? 'Gmail deletion jobs deleted successfully!'
            : 'Gmail deletion job deleted successfully!',
      });

      queryClient.setQueryData<GmailDeletionModel[] | undefined>(
        [UserKeys.GmailDeletionModels],
        (oldData) => {
          if (!oldData) return;
          return oldData.filter(
            (model) => !selectedModels.includes(model.created)
          );
        }
      );

      setSelectedGmailDeletions([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedModels.length > 1
            ? 'There was an issue deleting the selected gmail deletion jobs. Please refresh and try again.'
            : 'There was an issue deleting this gmail deletion job. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.import_export.gmail_deletion.delete_gmail_deletion,
        {
          module: ModuleList.modules.importExport.name,
          section: ModuleList.modules.importExport.sections.gmailDeletion,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.DeleteGmailSearch.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Delete Gmail Deletion${moreThanOneDeletions ? 's' : ''}`}
      dialogContent={
        <Box>
          <Typography>
            {moreThanOneDeletions ? (
              <>
                Delete <strong>{selectedModels.length}</strong> Gmail Deletion
                Jobs?
              </>
            ) : (
              <>Delete this Gmail Deletion Job?</>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: `Delete Gmail Deletion${moreThanOneDeletions ? 's' : ''}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
